import React, { useEffect, useMemo, useState } from "react";
import Canvas from "../../components/Canvas";
import apiClient from "../../actions/api";
import WorkspaceImage from "../../assets/resume-builder-workspace.png";
import InterviewImage from "../../assets/resume-builder-interview.png";
import ProductShowcaseDark from "../../assets/home-product-showcase-v4.png";
import ProductShowcaseLight from "../../assets/home-product-showcase-light-v4.png";

const productAppUrl =
  process.env.PRODUCT_APP_URL || process.env.REACT_APP_PRODUCT_APP_URL || "/";

const formatPrice = (value) =>
  `$${Number(value || 0)
    .toFixed(3)
    .replace(/0+$/, "")
    .replace(/\.$/, ".00")}`;

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const features = [
  [
    "01",
    "Build a resume for the role",
    "Paste a job description and turn your experience into a focused, ATS-ready resume in DOCX and PDF.",
    <path d="M7 3h7l4 4v14H7zM14 3v5h5M10 13h5M10 17h5" />,
  ],
  [
    "02",
    "Answer with confidence",
    "Generate polished, relevant answers to application questions using your resume, profile, and the role context.",
    <path d="M20 12a8 8 0 0 1-8 8H5l2-3.2A8 8 0 1 1 20 12ZM8.5 12h.01M12 12h.01M15.5 12h.01" />,
  ],
  [
    "03",
    "Track every application",
    "Keep job links, documents, statuses, follow-ups, and outcomes together in one clean application pipeline.",
    <path d="M4 19V9M10 19V5M16 19v-7M3 19h18" />,
  ],
  [
    "04",
    "Run interviews as a team",
    "Schedule each interview stage, coordinate teammates, review calendars, and see performance at a glance.",
    <path d="M7 3v3M17 3v3M4 8h16M5 5h14v15H5zM8 12h3M13 12h3M8 16h3" />,
  ],
];

const Product = () => {
  const [prices, setPrices] = useState({
    answer: 0.004,
    resume: 0.02,
    coverLetter: 0.005,
  });
  const [pricingLoading, setPricingLoading] = useState(true);

  useEffect(() => {
    document.title = "Resume Builder | Lovapex Tech";
  }, []);

  useEffect(() => {
    let isMounted = true;

    apiClient
      .get("/pricing")
      .then(({ data }) => {
        if (!isMounted) return;
        setPrices({
          answer: Number(data.answer),
          resume: Number(data.resume),
          coverLetter: Number(data.coverLetter),
        });
      })
      .catch(() => {
        console.warn(
          "Using fallback pricing because the pricing API is unavailable.",
        );
      })
      .finally(() => {
        if (isMounted) setPricingLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const exampleTotal = useMemo(
    () => prices.answer * 5 + prices.resume + prices.coverLetter,
    [prices],
  );

  return (
    <main className="product-page">
      <section className="product-hero">
        <Canvas />
        <div className="container product-hero-grid">
          <div className="product-hero-copy">
            <p className="eyebrow">Meet Resume Builder</p>
            <h1>Your entire job search. One intelligent workspace.</h1>
            <p className="product-lede">
              Create tailored resumes, answer applications with AI, organize
              every opportunity, and keep interviews moving—from first click to
              final offer.
            </p>
            <div className="actions">
              <a
                className="button button-primary button-lg product-launch"
                href={productAppUrl}
              >
                Start building free <ArrowIcon />
              </a>
              <a className="button button-secondary button-lg" href="#features">
                Explore features
              </a>
            </div>
            <div className="product-trust-row">
              <span>
                <CheckIcon /> No subscription
              </span>
              <span>
                <CheckIcon /> Pay as you go
              </span>
              <span>
                <CheckIcon /> Export DOCX & PDF
              </span>
            </div>
          </div>
          <div className="product-dashboard">
            <img
              className="product-dashboard-light"
              src={ProductShowcaseLight}
              alt="Resume Builder dashboard showing applications, interviews, analytics, and profile progress"
            />
            <img
              className="product-dashboard-dark"
              src={ProductShowcaseDark}
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      <section className="product-strip">
        <div className="container">
          <span>AI-tailored documents</span>
          <i />
          <span>Application tracking</span>
          <i />
          <span>Interview management</span>
          <i />
          <span>Team analytics</span>
        </div>
      </section>

      <section className="section product-story-section">
        <div className="container product-story-grid">
          <div className="product-story-copy">
            <p className="eyebrow">Less admin. More progress.</p>
            <h2>A clearer way to run your job search.</h2>
            <p>
              Job hunting gets complicated fast. Resume Builder brings the
              documents, opportunities, conversations, and next actions into a
              single workspace that stays organized for you.
            </p>
            <div className="product-story-stats">
              <div>
                <strong>1</strong>
                <span>workspace for your complete search</span>
              </div>
              <div>
                <strong>24/7</strong>
                <span>access to every document and update</span>
              </div>
              <div>
                <strong>0</strong>
                <span>monthly plans or long commitments</span>
              </div>
            </div>
          </div>
          <figure className="product-photo product-photo-workspace">
            <img
              src={WorkspaceImage}
              alt="Professional reviewing a resume beside the Resume Builder dashboard"
            />
            <figcaption>
              <span>Live workspace</span>
              <strong>Know what to do next.</strong>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section product-features" id="features">
        <div className="container">
          <div className="product-section-heading">
            <div>
              <p className="eyebrow">Built for momentum</p>
              <h2>Everything between “found it” and “got it.”</h2>
            </div>
            <p>
              Resume Builder replaces scattered documents, tabs, and calendars
              with one focused system that helps you take the right next step.
            </p>
          </div>
          <div className="product-feature-grid">
            {features.map(([number, title, copy, icon]) => (
              <article className="product-feature-card" key={number}>
                <div className="product-feature-top">
                  <span className="product-icon">
                    <svg viewBox="0 0 24 24">{icon}</svg>
                  </span>
                  <span>{number}</span>
                </div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section product-workflow-section">
        <div className="container product-workflow-grid">
          <div className="product-resume-card">
            <div className="product-resume-toolbar">
              <span />
              <span />
              <span />
              <b>Resume score</b>
              <strong>94</strong>
            </div>
            <div className="product-paper">
              <div className="product-paper-head">
                <span>AM</span>
                <div>
                  <strong>Alex Morgan</strong>
                  <small>Senior Product Engineer</small>
                </div>
              </div>
              <i className="wide" />
              <i />
              <i className="medium" />
              <h4>EXPERIENCE</h4>
              <i className="wide" />
              <i className="wide" />
              <i className="short" />
              <h4>CORE SKILLS</h4>
              <div className="product-skill-pills">
                <span>React</span>
                <span>Systems</span>
                <span>Leadership</span>
              </div>
            </div>
            <div className="product-match-badge">
              <CheckIcon />
              <div>
                <strong>Strong match</strong>
                <span>Tailored to the job description</span>
              </div>
            </div>
          </div>
          <div className="product-workflow-copy">
            <p className="eyebrow">Tailored, not templated</p>
            <h2>Give every application its best chance.</h2>
            <p>
              Bring the role and your experience together. Resume Builder
              identifies what matters, sharpens your story, and creates
              professional documents you can send with confidence.
            </p>
            <ul>
              <li>
                <CheckIcon />
                <span>
                  <strong>Job-specific resumes</strong> that highlight the
                  skills each employer is looking for.
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>Focused cover letters</strong> that connect your
                  background to the company’s needs.
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>Context-aware answers</strong> for the questions that
                  slow applications down.
                </span>
              </li>
            </ul>
            <a className="product-text-link" href={productAppUrl}>
              Create your first resume <ArrowIcon />
            </a>
          </div>
        </div>
      </section>

      <section className="section product-steps-section">
        <div className="container">
          <div className="product-centered-heading">
            <p className="eyebrow">From role to ready</p>
            <h2>Three steps to a stronger application.</h2>
            <p>
              No complicated setup. Start with the opportunity in front of you
              and let Resume Builder organize the work around it.
            </p>
          </div>
          <div className="product-steps-grid">
            <article>
              <span>01</span>
              <div className="product-step-line" />
              <h3>Add the opportunity</h3>
              <p>
                Paste the job URL and description so the workspace understands
                the role, company, and requirements.
              </p>
            </article>
            <article>
              <span>02</span>
              <div className="product-step-line" />
              <h3>Create your documents</h3>
              <p>
                Generate a tailored resume, cover letter, and application
                answers grounded in your real experience.
              </p>
            </article>
            <article>
              <span>03</span>
              <div className="product-step-line" />
              <h3>Track every next step</h3>
              <p>
                Move the application through your pipeline and keep interviews,
                follow-ups, and outcomes visible.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="section product-interview-section">
        <div className="container product-interview-grid">
          <figure className="product-photo product-photo-interview">
            <img
              src={InterviewImage}
              alt="Professional prepared for a remote job interview"
            />
            <div className="product-photo-calendar">
              <small>UP NEXT</small>
              <strong>Technical interview</strong>
              <span>Tomorrow · 10:30 AM</span>
            </div>
          </figure>
          <div className="product-interview-copy">
            <p className="eyebrow">Beyond the application</p>
            <h2>Stay ready when the interview arrives.</h2>
            <p>
              Your pipeline does not stop at “submitted.” See every upcoming
              conversation, interview stage, teammate, and follow-up in calendar
              and board views.
            </p>
            <div className="product-mini-features">
              <div>
                <span>01</span>
                <p>
                  <strong>Interview calendar</strong>See every stage and meeting
                  without switching tools.
                </p>
              </div>
              <div>
                <span>02</span>
                <p>
                  <strong>Team coordination</strong>Keep participants, notes,
                  and ownership connected.
                </p>
              </div>
              <div>
                <span>03</span>
                <p>
                  <strong>Performance insight</strong>Understand application and
                  interview activity over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="section product-pricing-section"
        aria-busy={pricingLoading}
      >
        <div className="container">
          <div className="product-pricing-head">
            <div>
              <p className="eyebrow">Simple by design</p>
              <h2>Pay only when Resume Builder creates value.</h2>
            </div>
            <p>
              No subscription, no monthly commitment, and no hidden platform
              fee. Add balance when you need it and use it at your own pace.
            </p>
          </div>
          <div className="product-pricing-grid">
            <article>
              <span>AI APPLICATION ANSWER</span>
              <strong>{formatPrice(prices.answer)}</strong>
              <small>per generated answer</small>
              <p>
                Polished answers based on your experience and the application
                question.
              </p>
            </article>
            <article className="featured">
              <span>TAILORED RESUME</span>
              <strong>{formatPrice(prices.resume)}</strong>
              <small>per resume</small>
              <p>
                A role-specific resume with professional DOCX and PDF exports
                included.
              </p>
            </article>
            <article>
              <span>TAILORED COVER LETTER</span>
              <strong>{formatPrice(prices.coverLetter)}</strong>
              <small>per cover letter</small>
              <p>
                A focused introduction personalized for the role and company.
              </p>
            </article>
          </div>
          <div className="product-value-example">
            <div>
              <span>A COMPLETE APPLICATION</span>
              <strong>5 answers + resume + cover letter</strong>
            </div>
            <div>
              <small>Total</small>
              <strong>{formatPrice(exampleTotal)}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="product-final-cta">
        <div className="container">
          <div>
            <p className="eyebrow">Your next opportunity is waiting</p>
            <h2>Turn your job search into a system that works.</h2>
            <p>
              Start with a tailored resume, then keep every application and
              interview moving in one place.
            </p>
          </div>
          <a
            className="button button-primary button-lg product-launch"
            href={productAppUrl}
          >
            Open Resume Builder <ArrowIcon />
          </a>
        </div>
      </section>
    </main>
  );
};

export default Product;
